/**
 * components/tabs/SettingsConnections.jsx
 *
 * upstox-073 — Settings → Account → Profile and Connections, as designed (the Claude Design
 * handoff). Layout only: the status reads, the credentials saves/removes, the sign-in links and
 * the FMP key behave exactly as they did (same requests, same messages).
 */

import React, { useEffect, useState } from 'react';
import { API, apiFetch } from './settingsApi';
import { Pill, MONO, monoStyle } from './SettingsParts';
import { openUpstoxSignin, onUpstoxSignedIn } from '../riskTerminal/signin';   // upstox-053

// ── Profile ──────────────────────────────────────────────────────────────────
const ROLE_LABELS = { owner: 'Owner', user: 'User', viewer: 'Viewer' };

export function ProfileContent({ user }) {
  if (!user) return null;
  const role = ROLE_LABELS[user.role] || (user.role?.replace(/_/g, ' ') ?? 'Unknown');
  const row = { display: 'grid', gridTemplateColumns: '200px 1fr', padding: '11px 0', borderBottom: '1px solid var(--hair)', fontSize: 13 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {user.picture
          ? <img src={user.picture} alt={user.name} style={{ width: 44, height: 44, borderRadius: '50%' }} />
          : <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#6B2FA0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 500 }}>
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{user.name}</span>
            <span data-role style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: 'var(--amber-ink)', background: 'var(--amber-bg)', padding: '2px 7px', borderRadius: 4 }}>{role.toUpperCase()}</span>
          </div>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', overflowWrap: 'anywhere' }}>{user.email} · signed in with Google</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--hair)' }}>
        <div style={row}><span style={{ color: 'var(--ink-2)' }}>Language</span><span>English (Indian)</span></div>
        <div style={row}><span style={{ color: 'var(--ink-2)' }}>Time zone</span><span>Asia/Kolkata <span style={{ color: 'var(--ink-2)' }}>(IST, UTC+5:30)</span></span></div>
      </div>
    </div>
  );
}

// ── the two connection statuses ──────────────────────────────────────────────
function useKiteStatus() {
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => apiFetch('/kite/status').then(r => r.json()).then(setS).catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);
  return s;
}

function useUpstoxStatus() {
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => apiFetch('/upstox/status').then(r => r.json()).then(setS).catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    // upstox-041: re-read on landing (above) and whenever the page regains focus,
    // so the status is never stale after an Upstox sign-in
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', load);
    const offSignin = onUpstoxSignedIn(load);              // upstox-053: signed in in another tab
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', load);
      offSignin();
    };
  }, []);
  return s;
}

const fmtExpiry = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso || '');
  if (!m) return '—';
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1];
  return `${Number(m[3])} ${mon}, ${m[4]}:${m[5]}`;
};

// ── "Manage credentials": one broker's key / secret / redirect URL ───────────
function Credentials({ broker, endpoint, help, onCreds, redirectNote, initialOpen }) {
  const [open,         setOpen]         = useState(!!initialOpen);
  const [creds,        setCreds]        = useState(null);
  const [credsLoading, setCredsLoading] = useState(true);
  const [apiKey,       setApiKey]       = useState('');
  const [apiSecret,    setApiSecret]    = useState('');
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState('');
  const [copied,       setCopied]       = useState(false);

  useEffect(() => {
    apiFetch(endpoint)
      .then(r => r.json())
      .then(d => setCreds(d))
      .catch(() => setCreds(null))
      .finally(() => setCredsLoading(false));
  }, [endpoint]);
  useEffect(() => { onCreds && onCreds(creds); }, [creds]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!(apiKey.trim() || apiSecret.trim());

  const handleSaveCreds = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) {
      setMsg('Both API Key and Secret are required.');
      return;
    }
    setSaving(true); setMsg('');
    try {
      const r = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey.trim(), api_secret: apiSecret.trim() }),
      });
      if (!r.ok) {
        const err = await r.json();
        setMsg(err.detail || 'Save failed');
      } else {
        const d = await r.json();
        setCreds({
          configured:   true,
          api_key_hint: d.api_key_hint,
          slug:         d.slug,
          redirect_url: d.redirect_url,
          login_url:    d.login_url,
        });
        setApiKey(''); setApiSecret('');
        setMsg('✓ Credentials saved');
      }
    } catch { setMsg('Network error — try again'); }
    finally { setSaving(false); }
  };

  const handleDeleteCreds = async () => {
    if (!window.confirm(`Remove your stored ${broker} credentials?`)) return;
    await apiFetch(endpoint, { method: 'DELETE' });
    setCreds({ configured: false });
    setMsg('Credentials removed');
  };

  const copyRedirect = () => {
    if (!creds?.redirect_url) return;
    navigator.clipboard?.writeText(creds.redirect_url)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1400); })
      .catch(() => {});
  };

  const label = { display: 'flex', flexDirection: 'column', gap: 1 };
  return (
    <div data-credentials={broker}>
      <button type="button" className="sx2-btn" aria-expanded={open} onClick={() => setOpen(o => !o)} style={{ height: 28, fontSize: 12 }}>
        {open ? 'Hide credentials' : 'Manage credentials'}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, borderRadius: 8, background: 'var(--surface-2)', marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>Your API credentials</span>
            {!credsLoading && (creds?.configured
              ? <span style={{ fontSize: 12, color: 'var(--up-field-ink)' }}>Saved · key <span style={{ fontFamily: MONO }}>{creds.api_key_hint}</span> · encrypted</span>
              : <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>not configured</span>)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>{help}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', gap: '10px 14px', alignItems: 'center' }}>
            <div style={label}><span style={{ fontSize: 13 }}>Redirect URL</span><span style={{ fontSize: 11, color: 'var(--ink-2)' }}>from the terminal</span></div>
            <div style={{ display: 'flex', gap: 8, minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0, height: 32, display: 'flex', alignItems: 'center', padding: '0 10px', background: 'var(--surface-3)', border: '1px solid var(--hair-strong)',
                borderRadius: 6, fontFamily: MONO, fontSize: 12, color: 'var(--ink-2)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {creds?.redirect_url || 'shown once your credentials are saved'}
              </div>
              <button type="button" className="sx2-btn" style={{ height: 32 }} disabled={!creds?.redirect_url} onClick={copyRedirect}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div style={label}><span style={{ fontSize: 13 }}>API key</span><span style={{ fontSize: 11, color: 'var(--ink-2)' }}>from your app</span></div>
            <input type="text" className="sx2-input" placeholder={`${broker} API key`} value={apiKey} onChange={e => setApiKey(e.target.value)} style={{ fontFamily: MONO, fontSize: 13 }} />
            <div style={label}><span style={{ fontSize: 13 }}>API secret</span><span style={{ fontSize: 11, color: 'var(--ink-2)' }}>from your app</span></div>
            <input type="password" className="sx2-input" placeholder={`${broker} API secret`} value={apiSecret} onChange={e => setApiSecret(e.target.value)} style={{ fontFamily: MONO, fontSize: 13 }} />
            <span />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="sx2-primary" data-save-credentials disabled={!dirty || saving} onClick={handleSaveCreds}>{saving ? 'Saving…' : 'Save credentials'}</button>
              <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{redirectNote}</span>
              <span style={{ flex: 1 }} />
              {creds?.configured && (
                <button type="button" onClick={handleDeleteCreds} style={{ background: 'none', border: 'none', color: 'var(--down-field-ink)', fontSize: 12, cursor: 'pointer' }}>Remove</button>
              )}
            </div>
          </div>
          {msg && <div style={{ fontSize: 12, color: msg.startsWith('✓') ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>{msg}</div>}
        </div>
      )}
    </div>
  );
}

const cardRow = { padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 };

function CardHead({ name, pill, sub, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 14, fontWeight: 600 }}>{name}</span>{pill}</div>
        <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{sub}</span>
      </div>
      {action}
    </div>
  );
}

function KiteCard({ kiteStatus, open }) {
  const connected = kiteStatus?.connected;
  const sessions = kiteStatus?.sessions || [];
  const [loginUrl, setLoginUrl] = useState(null);     // the user's own app login link, once his credentials are read
  const KITE_LOGIN_URL = loginUrl || `${API}/kite/login`;
  return (
    <div data-card="kite" style={cardRow}>
      <CardHead name="Kite" sub="Zerodha · main data feed"
        pill={kiteStatus ? <Pill kind={connected ? 'ok' : 'bad'} dot>{connected ? 'Connected' : 'Not connected'}</Pill> : <Pill>…</Pill>}
        action={<a className="sx2-btn" href={KITE_LOGIN_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--ink)' }}>{connected ? 'Re-connect' : 'Connect'}</a>} />
      {sessions.length > 0 && (
        <>
          <div className="sx2-scroll" style={{ border: '1px solid var(--hair)', borderRadius: 8, background: 'var(--surface)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', fontSize: 13, minWidth: 520 }}>
            {sessions.map((s, i) => (
              <div key={s.google_id} data-session style={{ display: 'grid', gridTemplateColumns: '84px 1fr 110px 170px', gap: 12, padding: '9px 14px', alignItems: 'center', borderTop: i ? '1px solid var(--hair)' : 'none' }}>
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: s.active ? 'var(--up-field-ink)' : s.live ? 'var(--ink-2)' : 'var(--down-field-ink)' }}>
                  {s.active ? 'ACTIVE' : (s.live ? 'STANDBY' : 'EXPIRED')}</span>
                <span style={{ fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere' }}>{s.user_name || s.name || s.email || s.google_id}</span>
                <span style={monoStyle}>{s.kite_user_id || ''}</span>
                <span style={{ color: 'var(--ink-2)', textAlign: 'right' }}>expires {fmtExpiry(s.expires_at)}</span>
              </div>
            ))}
          </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            One healthy session feeds the whole terminal. If the active one drops, the standby takes over automatically. Re-connect opens Zerodha login in a new tab.
          </div>
        </>
      )}
      <Credentials broker="Kite" endpoint="/api/settings/kite" initialOpen={open}
        onCreds={c => setLoginUrl(c?.login_url || null)}
        redirectNote="Paste the redirect URL into your app in the Zerodha console first."
        help={<>Each Kite Connect app is tied to one Client ID, so you need your own app at{' '}
          <a href="https://developers.kite.trade" target="_blank" rel="noreferrer">developers.kite.trade</a> with live + historical data enabled.</>} />
    </div>
  );
}

function UpstoxCard({ upstoxStatus, open }) {
  const connected = upstoxStatus?.connected;
  const [loginUrl, setLoginUrl] = useState(null);
  // upstox-053: sign-in opens in a new tab (return target `popup`) and this tab updates on its message
  const UPSTOX_LOGIN_URL = (loginUrl || `${API}/upstox/login`) + '?return_to=popup';
  return (
    <div data-card="upstox" style={{ ...cardRow, borderTop: '1px solid var(--hair)' }}>
      <CardHead name="Upstox" sub="Broker · trading account"
        pill={upstoxStatus ? <Pill kind={connected ? 'ok' : 'bad'} dot>{connected ? 'Connected' : 'Not connected'}</Pill> : <Pill>…</Pill>}
        action={<a className="sx2-btn" href={UPSTOX_LOGIN_URL} target="_blank" rel="noopener noreferrer" onClick={e => openUpstoxSignin(e, UPSTOX_LOGIN_URL)} style={{ color: 'var(--ink)' }}>{connected ? 'Re-connect' : 'Connect'}</a>} />
      <Credentials broker="Upstox" endpoint="/api/settings/upstox" initialOpen={open}
        onCreds={c => setLoginUrl(c?.login_url || null)}
        redirectNote="Paste the redirect URL into your app in the Upstox console first."
        help={<>Upstox ties each app to one account. Create yours at{' '}
          <a href="https://account.upstox.com/developer/apps" target="_blank" rel="noreferrer">account.upstox.com/developer/apps</a> (App type: Algo Trading).</>} />
    </div>
  );
}

const FMP_LS_KEY = 'fmp_api_key';
function FmpCard() {
  const read = () => { try { return localStorage.getItem(FMP_LS_KEY) || ''; } catch (e) { return ''; } };
  const [key,   setKey]   = useState(read);
  const [saved, setSaved] = useState(!!read());
  const [flash, setFlash] = useState(false);
  const handleSave = () => {
    const trimmed = key.trim();
    try {
      if (trimmed) localStorage.setItem(FMP_LS_KEY, trimmed);
      else         localStorage.removeItem(FMP_LS_KEY);
    } catch (e) { /* a blocked store: the key stays for this page only */ }
    setSaved(!!trimmed);
    setFlash(true);
    setTimeout(() => setFlash(false), 2000);
  };
  return (
    <div data-card="fmp" style={{ ...cardRow, gap: 12, borderTop: '1px solid var(--hair)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>Financial Modeling Prep</span>
        <Pill kind={saved ? 'ok' : 'quiet'}>{saved ? 'Set' : 'Not set'}</Pill>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input type="password" className="sx2-input" placeholder="FMP API key" value={key} onChange={e => { setKey(e.target.value); setFlash(false); }} style={{ flex: 1, maxWidth: 360 }} />
        <button type="button" className="sx2-btn" style={{ height: 32 }} onClick={handleSave}>{flash ? '✓ Saved' : 'Save'}</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        Used by Economic Calendar → Refresh US Events. Free tier at{' '}
        <a href="https://financialmodelingprep.com" target="_blank" rel="noreferrer">financialmodelingprep.com</a>, or set{' '}
        <span style={{ fontFamily: MONO }}>FMP_API_KEY</span> in Railway env vars.
      </div>
    </div>
  );
}

export function ConnectionsContent({ isOwner, openCreds }) {
  const kiteStatus = useKiteStatus();
  const upstoxStatus = useUpstoxStatus();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--hair)', borderRadius: 10, background: 'var(--surface)' }}>
      <KiteCard kiteStatus={kiteStatus} open={openCreds} />
      <UpstoxCard upstoxStatus={upstoxStatus} open={openCreds} />
      {isOwner && <FmpCard />}
    </div>
  );
}
